import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { Project, Stage, Substage, Task } from '@/types/projects';

interface ProjectState {
  projects: Project[];
  stages: Stage[];
  substages: Substage[];
  tasks: Task[];
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  fetchProjects: () => Promise<void>;
  fetchProjectDetails: (projectId: string) => Promise<void>;
  createProject: (data: Partial<Project>) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  createStage: (data: Partial<Stage>) => Promise<Stage>;
  updateStage: (id: string, data: Partial<Stage>) => Promise<void>;
  deleteStage: (id: string) => Promise<void>;
  createSubstage: (data: Partial<Substage>) => Promise<Substage>;
  updateSubstage: (id: string, data: Partial<Substage>) => Promise<void>;
  deleteSubstage: (id: string) => Promise<void>;
  updateTask: (id: string, data: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  stages: [],
  substages: [],
  tasks: [],
  isLoading: false,
  error: null,
  initialized: false,

  createProject: async (data: Partial<Project>) => {
    try {
      set({ isLoading: true, error: null });
      
      // Create project
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert([{
          name: data.name,
          description: data.description,
          start_date: data.start_date,
          end_date: data.end_date,
          priority: data.priority,
          owner_id: data.owner_id,
          template_id: data.template_id,
          status: 'planning'
        }])
        .select(`
          *,
          owner:owner_id (
            id,
            full_name,
            email
          ),
          template:template_id (
            id,
            name
          )
        `)
        .single();

      if (projectError) throw projectError;

      // Update local state
      set(state => ({
        projects: [newProject, ...state.projects]
      }));

      return newProject;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateProject: async (id: string, data: Partial<Project>) => {
    try {
      set({ isLoading: true, error: null });
      
      // Format data for database
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };
      
      // Use snake_case fields for database
      if (data.start_date) updateData.start_date = data.start_date;
      if (data.end_date) updateData.end_date = data.end_date;
      if (data.owner_id) updateData.owner_id = data.owner_id;
      if (data.template_id) updateData.template_id = data.template_id;

      // Update in database
      const { error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        projects: state.projects.map(project =>
          project.id === id ? { ...project, ...data } : project
        )
      }));
      
      // Return void to match the interface
      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update project';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchProjects: async () => {
    // Skip if already initialized
    if (get().initialized && get().projects.length > 0) return;

    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          owner:owner_id (
            id,
            full_name,
            email
          ),
          template:template_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ projects: data || [], initialized: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch projects';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchProjectDetails: async (projectId: string) => {
    try {
      set({ isLoading: true, error: null });

      // Fetch stages with departments
      const { data: stages, error: stagesError } = await supabase
        .from('stages')
        .select(`
          *,
          departments:stage_departments(department_id)
        `)
        .eq('project_id', projectId)
        .order('order_num', { ascending: true });

      if (stagesError) throw stagesError;

      // Transform stages data
      const transformedStages = stages.map(stage => ({
        ...stage,
        department_ids: stage.departments.map((d: any) => d.department_id)
      }));

      // Fetch substages
      const { data: substages, error: substagesError } = await supabase
        .from('substages')
        .select('*')
        .in('stage_id', transformedStages.map(s => s.id))
        .order('order_num', { ascending: true });

      if (substagesError) throw substagesError;

      // Fetch tasks
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .in('substage_id', substages.map(s => s.id))
        .order('created_at', { ascending: true });

      if (tasksError) throw tasksError;

      // Update state while preserving existing data
      set(state => ({
        stages: [...state.stages.filter(s => s.project_id !== projectId), ...transformedStages],
        substages: [...state.substages.filter(s => !transformedStages.find(ts => ts.id === s.stage_id)), ...substages],
        tasks: [...state.tasks.filter(t => !substages.find(s => s.id === t.substage_id)), ...tasks],
        isLoading: false,
        error: null
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch project details';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  createStage: async (data: Partial<Stage>) => {
    try {
      set({ isLoading: true, error: null });

      // Get max order number for the project
      const { data: maxOrderResult } = await supabase
        .from('stages')
        .select('order_num')
        .eq('project_id', data.project_id)
        .order('order_num', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrder = (maxOrderResult?.order_num || 0) + 1;

      // Create stage
      const { data: newStage, error: stageError } = await supabase
        .from('stages')
        .insert([{
          ...data,
          order_num: nextOrder,
          status: 'pending'
        }])
        .select()
        .single();

      if (stageError) throw stageError;

      // Update local state
      set(state => ({
        stages: [...state.stages, newStage]
      }));

      return newStage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create stage';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  createSubstage: async (data: Partial<Substage>) => {
    try {
      set({ isLoading: true, error: null });

      // Get max order number for the stage
      const { data: maxOrderResult } = await supabase
        .from('substages')
        .select('order_num')
        .eq('stage_id', data.stage_id)
        .order('order_num', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrder = (maxOrderResult?.order_num || 0) + 1;

      // Create substage
      const { data: newSubstage, error: substageError } = await supabase
        .from('substages')
        .insert([{
          ...data,
          order_num: nextOrder,
          status: 'pending'
        }])
        .select()
        .single();

      if (substageError) throw substageError;

      // Update local state
      set(state => ({
        substages: [...state.substages, newSubstage]
      }));

      return newSubstage;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create substage';
      set({ error: message });
      throw new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  updateStage: async (id: string, data: Partial<Stage>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('stages')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        stages: state.stages.map(stage =>
          stage.id === id ? { ...stage, ...data } : stage
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update stage';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteStage: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('stages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        stages: state.stages.filter(stage => stage.id !== id),
        substages: state.substages.filter(substage => substage.stage_id !== id),
        tasks: state.tasks.filter(task => 
          !state.substages.find(s => s.stage_id === id && s.id === task.substage_id)
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete stage';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateSubstage: async (id: string, data: Partial<Substage>) => {
    try {
      const { error } = await supabase
        .from('substages')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        substages: state.substages.map(substage =>
          substage.id === id ? { ...substage, ...data } : substage
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update substage';
      throw new Error(message);
    }
  },

  deleteSubstage: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('substages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        substages: state.substages.filter(substage => substage.id !== id),
        tasks: state.tasks.filter(task => task.substage_id !== id)
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete substage';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateTask: async (id: string, data: Partial<Task>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('tasks')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.map(task =>
          task.id === id ? { ...task, ...data } : task
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update task';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteTask: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        tasks: state.tasks.filter(task => task.id !== id)
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete task';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('project_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'projects' },
    () => {
      useProjectStore.getState().fetchProjects();
    }
  )
  .subscribe();

// Initial data fetching is handled by page components on mount via useEffect
